import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getPostClosingFields, updatePostClosingFields } from 'services'
import { InputConvert, InputValidate, openAuditLog } from 'utils'
import { getInvisibleFields } from 'utils/loan'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { CollateralReviewConfig, flattenConfig } from './constants'

export default function CollateralReview() {
  const [changed, setChanged] = useState(false)
  const [inputs, setInputs] = useState<Record<string, any>>({})
  const [isLoading, setLoading] = useState(false)
  const [baseInputs, setBaseInputs] = useState<Record<string, any>>({})

  const withNoteKey = (key: string) => `${key}Notes`

  useEffect(() => {
    const currentLoanNumber = setLoanNumber()
    setLoading(true)

    getPostClosingFields('collateralReview')
      .then((loanData) => {
        const config = loanData._config as CollateralReviewConfig
        const merged = flattenConfig(config)
        setBaseInputs(merged)

        const nInputs: Record<string, any> = cloneDeep(merged)
        Object.keys(merged).forEach((key) => {
          const input = merged[key]
          if (!input || input.inputType === 'section') return
          const noteKey = withNoteKey(key)
          nInputs[noteKey] = nInputs[noteKey] || { inputType: 'text', title: 'Notes' }
        })

        if (currentLoanNumber) {
          nInputs.LoanNumber = { ...(nInputs.LoanNumber || {}), value: currentLoanNumber, readOnly: true }
        }

        Object.keys(nInputs).forEach((key) => {
          if (key in loanData && key !== '_config') nInputs[key].value = loanData[key]
        })
        if (!nInputs.LoanNumber?.value && currentLoanNumber) {
          nInputs.LoanNumber = { ...(nInputs.LoanNumber || {}), value: currentLoanNumber, readOnly: true }
        }

        setInputs(nInputs)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const invisibleFields = useMemo(() => {
    const fieldValues: Record<string, any> = {}
    Object.keys(baseInputs).forEach((key) => {
      fieldValues[key] = inputs[key]?.value
    })
    return getInvisibleFields('CollateralReview' as any, null, {
      additionalFieldProps: {
        CollateralReview: baseInputs,
      },
      additionalFieldValues: {
        CollateralReview: fieldValues,
      },
    })
  }, [baseInputs, inputs])

  const onChange = (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    const converted = InputConvert(newInputs[key], value)
    newInputs[key].value = converted
    newInputs[key].error = InputValidate(newInputs[key])
    setInputs(newInputs)
    setChanged(true)
  }

  const onSave = async () => {
    const payload: Record<string, any> = {}
    Object.keys(inputs).forEach((key) => {
      payload[key] = inputs[key].value
    })
    setLoading(true)
    setChanged(false)
    try {
      await updatePostClosingFields('collateralReview', payload)
      toast('Successfully updated', { type: 'info' })
    } catch (e) {
      // ignore
    } finally {
      setLoading(false)
    }
  }

  const renderFields = () => {
    const allKeys = Object.keys(baseInputs)
    return allKeys.map((key) => {
      const input = inputs[key]
      if (!input || baseInputs[key]?.inParentSection === true) return null
      if (invisibleFields.includes(key)) return null

      if (input.inputType === 'section') {
        const sectionIndex = allKeys.indexOf(key)
        const nextSectionIndex = allKeys.findIndex((k, i) => i > sectionIndex && baseInputs[k]?.inputType === 'section')
        const fieldsInSection = allKeys.slice(sectionIndex + 1, nextSectionIndex === -1 ? undefined : nextSectionIndex)
        const fieldsInSectionTitle = fieldsInSection.filter((k) => baseInputs[k]?.inParentSection === true)

        let additionalElements: JSX.Element | undefined = undefined
        if (fieldsInSectionTitle.length) {
          additionalElements = (
            <div className="flex flex-1 ml-8 gap-2 items-center">
              {fieldsInSectionTitle.map((key) => (
                <RenderInput
                  key={`${key}`}
                  input={{ ...inputs[key], history: true }}
                  Key={key}
                  onChange={onChange}
                  showHistory={showHistory}
                />
              ))}
            </div>
          )
        }
        return (
          <div key={key} className="md:col-span-2">
            <RenderInput input={{ ...input, additionalElements }} Key={key} onChange={onChange} />
          </div>
        )
      }
      const noteKey = withNoteKey(key)
      if (key === 'isMersLoan') {
        return (
          <div key={key} className="md:col-span-2">
            <RenderInput input={{ ...input, history: true }} Key={key} onChange={onChange} showHistory={showHistory} />
          </div>
        )
      }
      return (
        <div key={key} className="md:col-span-2">
          <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
            <div>
              <RenderInput
                input={{ ...input, history: true }}
                Key={key}
                onChange={onChange}
                showHistory={showHistory}
              />
            </div>
            <div>
              <RenderInput
                input={{ ...inputs[noteKey], history: true }}
                Key={noteKey}
                onChange={onChange}
                showHistory={showHistory}
              />
            </div>
          </div>
        </div>
      )
    })
  }

  const showHistory = (key: string) => {
    const options = {
      table: 'CollateralReview',
      field: key,
      keys: {
        field: key,
        menu: 'collateralReview',
      },
    }
    openAuditLog(options)
  }

  return (
    <div className="collateral-review-container">
      <SaveChanges onSave={onSave} show={changed} />
      <div className="max-w-screen-2xl m-auto">
        <div className="bg-white">
          <div className="grid gap-5 md:grid-cols-2 grid-cols-1">
            <LayoutLoading show={isLoading} />
            <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
              <span>Collateral Review</span>
            </h2>

            {renderFields()}
          </div>
        </div>
      </div>
    </div>
  )
}
